import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import { appConfig } from '@/config'
import type { UserProfileDto } from '@/core/localAuth'

/**
 * Sensitive field names that must be scrubbed from Sentry events and breadcrumbs
 */
const SENSITIVE_KEYS = new Set([
    'password',
    'password_confirmation',
    'newpassword',
    'currentpassword',
    'token',
    'access_token',
    'refresh_token',
    'authorization',
    'auth',
    'secret',
    'secret_key',
    'client_secret',
    'api_key',
    'apikey',
    'credit_card',
    'ssn',
])

/**
 * Recursively sanitize an object by masking sensitive keys and truncating oversized strings
 */
function sanitizePayload(data: unknown, depth = 0): unknown {
    if (depth > 5 || !data) return data

    if (typeof data === 'string') {
        // Strip Bearer tokens from strings and truncate oversized error payloads
        const redacted = data.replace(/Bearer\s+[A-Za-z0-9._-]+/gi, 'Bearer [REDACTED]')
        return redacted.length > 2000 ? redacted.slice(0, 2000) + '... [TRUNCATED]' : redacted
    }

    if (Array.isArray(data)) {
        return data.map((item) => sanitizePayload(item, depth + 1))
    }

    if (typeof data === 'object') {
        const sanitized: Record<string, unknown> = {}
        for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
            if (SENSITIVE_KEYS.has(key.toLowerCase())) {
                sanitized[key] = '[REDACTED]'
            } else {
                sanitized[key] = sanitizePayload(value, depth + 1)
            }
        }
        return sanitized
    }

    return data
}

/**
 * Sanitize URL query parameters that might contain sensitive tokens
 */
function sanitizeUrl(url?: string): string | undefined {
    if (!url) return url
    try {
        const parsed = new URL(url, window.location.origin)
        const params = parsed.searchParams
        for (const key of Array.from(params.keys())) {
            if (SENSITIVE_KEYS.has(key.toLowerCase())) {
                params.set(key, '[REDACTED]')
            }
        }
        return parsed.pathname + (params.toString() ? `?${params.toString()}` : '')
    } catch {
        return url
    }
}

/**
 * Scrub sensitive headers and URLs from outbound Sentry events
 */
function sanitizeSentryEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
    if (event.request) {
        if (event.request.headers) {
            delete event.request.headers.Authorization
            delete event.request.headers.authorization
            delete event.request.headers.cookie
            delete event.request.headers.Cookie
        }
        if (event.request.url) {
            event.request.url = sanitizeUrl(event.request.url)
        }
        if (event.request.data) {
            event.request.data = sanitizePayload(event.request.data)
        }
    }
    return event
}

/**
 * Scrub sensitive details from breadcrumbs
 */
function sanitizeSentryBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
    if (breadcrumb.data) {
        if (breadcrumb.data.url) {
            breadcrumb.data.url = sanitizeUrl(breadcrumb.data.url)
        }
        breadcrumb.data = sanitizePayload(breadcrumb.data) as Record<string, unknown>
    }
    return breadcrumb
}

let isSentryActive = false

/**
 * Initialize Sentry Vue SDK
 */
export function initSentry({ app, router }: { app: App; router?: Router }): boolean {
    const dsn = appConfig.sentryDsn?.trim()

    if (!dsn) {
        // Disabled when no DSN configured (normal in local development)
        return false
    }

    try {
        Sentry.init({
            app,
            dsn,
            environment: appConfig.sentryEnvironment,
            release: 'rws-web-ui@0.1.0',
            integrations: [
                ...(router ? [Sentry.browserTracingIntegration({ router })] : []),
            ],
            // Sample rate for performance tracing
            tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,

            // Scrub sensitive information from errors and breadcrumbs
            beforeSend(event) {
                return sanitizeSentryEvent(event)
            },
            beforeBreadcrumb(breadcrumb) {
                return sanitizeSentryBreadcrumb(breadcrumb)
            },
        })

        // Track active module and route name on navigation
        if (router && typeof router.afterEach === 'function') {
            router.afterEach((to) => {
                const firstSegment = to.path.split('/').filter(Boolean)[0] || 'root'
                Sentry.setTag('module', firstSegment)
                if (to.name) {
                    Sentry.setTag('route_name', String(to.name))
                }
            })
        }

        isSentryActive = true
        console.log('[Sentry] Initialized in environment:', appConfig.sentryEnvironment)
        return true
    } catch (err) {
        console.error('[Sentry] Failed to initialize:', err)
        return false
    }
}

/**
 * Check whether Sentry has been initialized with an active DSN
 */
export function isSentryInitialized(): boolean {
    return isSentryActive
}

/**
 * Associate the authenticated user and organization with Sentry events
 */
export function setSentryUser(user: UserProfileDto | null, organizationId?: string | null) {
    if (!user) {
        clearSentryUser()
        return
    }

    const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email
    Sentry.setUser({
        id: user.id,
        email: user.email,
        username: displayName,
    })

    if (organizationId) {
        Sentry.setTag('organization_id', organizationId)
    }

    if (user.isSuperAdmin !== undefined) {
        Sentry.setTag('is_super_admin', String(user.isSuperAdmin))
    }
}

/**
 * Clear user context from Sentry on logout
 */
export function clearSentryUser() {
    Sentry.setUser(null)
    Sentry.setTag('organization_id', undefined)
    Sentry.setTag('is_super_admin', undefined)
}

export interface ApiErrorContext {
    endpoint?: string
    method?: string
    status?: number
    data?: unknown
}

/**
 * Capture an API error (e.g. 400 Bad Request, 422 Unprocessable Entity, 5xx server error)
 * with structured metadata for debugging and autofixing.
 */
export function captureApiException(error: unknown, context?: ApiErrorContext) {
    const isClientError = typeof context?.status === 'number' && context.status >= 400 && context.status < 500

    Sentry.withScope((scope) => {
        if (context?.endpoint) {
            scope.setTag('api_endpoint', sanitizeUrl(context.endpoint) || '')
        }
        if (context?.method) {
            scope.setTag('api_method', context.method.toUpperCase())
        }
        if (context?.status) {
            scope.setTag('api_status', String(context.status))
        }

        scope.setTag('error_type', isClientError ? 'invalid_api_call' : 'api_server_error')
        scope.setLevel(isClientError ? 'warning' : 'error')

        if (context?.data) {
            scope.setContext('api_response', sanitizePayload(context.data) as Record<string, unknown>)
        }

        const exceptionToCapture = error instanceof Error
            ? error
            : new Error(`API Error ${context?.status ?? ''}: ${context?.endpoint ?? 'Unknown endpoint'}`)

        Sentry.captureException(exceptionToCapture)
    })
}

/**
 * General helper to capture an exception with optional extra context
 */
export function captureException(error: unknown, context?: Record<string, unknown>) {
    if (context) {
        Sentry.withScope((scope) => {
            scope.setContext('custom_context', sanitizePayload(context) as Record<string, unknown>)
            Sentry.captureException(error)
        })
    } else {
        Sentry.captureException(error)
    }
}

/**
 * Add a custom breadcrumb
 */
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
    Sentry.addBreadcrumb(breadcrumb)
}
